import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation, Trans } from "react-i18next";
import {
  ArrowLeft, MapPin, Languages, Briefcase, Award, GraduationCap, CalendarClock,
  Check, Target, Star, MessageSquare, FileText, ExternalLink, Wallet, AlertTriangle,
} from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { UserAvatar, Stars, VerifiedBadge, Pill, Spinner, EmptyState } from "@/components/ui-bits";
import { SocialLinks } from "@/components/Socials";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { fmtDate, fmtTime, fmtMoney } from "@/lib/constants";

export default function ExpertProfile() {
  const { t } = useTranslation();
  const { expertId } = useParams();
  const navigate = useNavigate();
  const [expert, setExpert] = useState(null);
  const [needs, setNeeds] = useState([]);
  const [needId, setNeedId] = useState("");
  const [slotId, setSlotId] = useState("");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [budget, setBudget] = useState(null);

  useEffect(() => {
    api.get(`/experts/${expertId}`).then(({ data }) => setExpert(data)).catch(() => setExpert(false));
    api.get("/needs/mine").then(({ data }) => {
      const appr = data.filter((n) => n.status === "approved");
      setNeeds(appr);
      if (appr[0]) setNeedId(appr[0].need_id);
    }).catch(() => {});
    api.get("/employee/budget").then(({ data }) => setBudget(data)).catch(() => {});
  }, [expertId]);

  const slotsByDay = useMemo(() => {
    const open = (expert?.availability || []).filter((s) => !s.booked && !s.busy && new Date(s.start) > new Date());
    const grouped = {};
    open.sort((a, b) => new Date(a.start) - new Date(b.start)).forEach((s) => {
      const k = new Date(s.start).toDateString();
      (grouped[k] = grouped[k] || []).push(s);
    });
    return grouped;
  }, [expert]);

  const dayRate = expert && expert.corp_day_rate;
  const dayHours = (expert && expert.day_hours) || 8;
  const currency = (expert && expert.currency) || "EUR";
  const selectedSlot = useMemo(() => (expert?.availability || []).find((s) => s.id === slotId), [expert, slotId]);
  const sessionHours = selectedSlot ? Math.max(0.5, Math.round(((new Date(selectedSlot.end) - new Date(selectedSlot.start)) / 3600000) * 10) / 10) : null;
  const sessionCost = (dayRate && sessionHours) ? Math.round((dayRate * sessionHours) / dayHours) : null;
  const hasBudget = !!(budget && budget.has_allocation);
  const overBudget = hasBudget && sessionCost != null && sessionCost > budget.remaining;

  const book = async () => {
    if (!needId) { toast.error(t("emp.profile.toastSelectGoal")); return; }
    if (!slotId) { toast.error(t("emp.profile.toastPickSlot")); return; }
    setBusy(true);
    try {
      await api.post("/bookings", { expert_id: expertId, need_id: needId, slot_id: slotId, topic });
      toast.success(t("emp.profile.toastRequested"));
      navigate("/app");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  if (expert === null) return <AppShell><div className="grid place-items-center py-32"><Spinner className="text-primary w-7 h-7" /></div></AppShell>;
  if (expert === false) return <AppShell><EmptyState title={t("emp.profile.notFound")} description={t("emp.profile.notFoundDesc")} action={<Link to="/experts"><Button variant="outline" className="rounded-lg">{t("emp.profile.backDirectory")}</Button></Link>} /></AppShell>;

  const dayKeys = Object.keys(slotsByDay);

  return (
    <AppShell>
      <Link to="/experts" data-testid="back-directory" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft size={16} /> {t("emp.profile.back")}
      </Link>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6 items-start">
        <div className="space-y-5">
          <div className="card-milled rounded-[0.8rem] p-6 sm:p-7 animate-fade-up">
            <div className="flex items-start gap-4">
              <UserAvatar src={fileUrl(expert.picture)} name={expert.name} size={72} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-xl font-semibold tracking-tight">{expert.name}</h1>
                  {expert.verified && <VerifiedBadge />}
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">{expert.title}</p>
                <div className="mt-2.5 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
                  <Stars value={expert.rating_avg} count={expert.rating_count} size={14} />
                  <span className="inline-flex items-center gap-1"><MapPin size={13} /> {expert.location || t("emp.profile.remote")}</span>
                  <span className="inline-flex items-center gap-1"><Briefcase size={13} /> {t("emp.profile.years", { count: expert.years_experience })}</span>
                </div>
              </div>
            </div>
            <p className="text-[15px] text-foreground mt-4 leading-relaxed">{expert.headline}</p>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {(expert.fields || []).map((f) => <Pill key={f} className="bg-primary/10 text-primary">{f}</Pill>)}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <SocialLinks socials={expert.socials} />
              {expert.cv_url && (
                <a href={fileUrl(expert.cv_url)} target="_blank" rel="noreferrer" data-testid="profile-cv"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 h-9 text-sm font-medium hover:border-primary/40 hover:text-primary transition-colors">
                  <FileText size={15} /> {t("emp.profile.viewCv")} <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>

          {expert.bio && (
            <Section title={t("emp.profile.about")} icon={MessageSquare} className="d1">
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{expert.bio}</p>
            </Section>
          )}

          {expert.expertise?.length > 0 && (
            <Section title={t("emp.profile.expertise")} icon={Target} className="d2">
              <div className="flex flex-wrap gap-2">{expert.expertise.map((x) => <Pill key={x} className="bg-secondary">{x}</Pill>)}</div>
            </Section>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            {expert.languages?.length > 0 && (
              <Section title={t("emp.profile.languages")} icon={Languages} className="d3">
                <p className="text-sm text-muted-foreground">{expert.languages.join(", ")}</p>
              </Section>
            )}
            {expert.certifications?.length > 0 && (
              <Section title={t("emp.profile.certifications")} icon={GraduationCap} className="d3">
                <ul className="space-y-1.5">{expert.certifications.map((c) => <li key={c} className="text-sm text-muted-foreground flex gap-2"><Check size={15} className="text-success shrink-0 mt-0.5" /> {c}</li>)}</ul>
              </Section>
            )}
          </div>

          {expert.achievements?.length > 0 && (
            <Section title={t("emp.profile.achievements")} icon={Award} className="d4">
              <ul className="space-y-1.5">{expert.achievements.map((a) => <li key={a} className="text-sm text-muted-foreground flex gap-2"><Award size={15} className="text-warning shrink-0 mt-0.5" /> {a}</li>)}</ul>
            </Section>
          )}

          {expert.reviews?.length > 0 && (
            <Section title={t("emp.profile.reviews", { count: expert.reviews.length })} icon={Star} className="d4">
              <div className="space-y-3">
                {expert.reviews.map((r, i) => (
                  <div key={`${r.name}-${i}`} className="rounded-lg border border-border p-4">
                    <div className="flex items-center gap-2">
                      <Stars value={r.rating} size={13} />
                      <span className="text-sm font-medium">{r.name}</span>
                    </div>
                    {r.comment && <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">"{r.comment}"</p>}
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        <div className="lg:sticky lg:top-24 animate-fade-up d2">
          <div className="card-milled rounded-[0.8rem] p-6 sm:p-7">
            <div className="flex items-center gap-3 mb-1">
              <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0"><CalendarClock size={17} strokeWidth={1.75} /></span>
              <div>
                <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground">{t("emp.profile.bookSession")}</h3>
                <p className="text-xs text-muted-foreground">{t("emp.profile.bookSessionSub")}</p>
              </div>
            </div>

            {needs.length === 0 ? (
              <div className="mt-4 rounded-lg border border-dashed border-border bg-muted/30 p-5 text-center">
                <p className="text-sm text-muted-foreground"><Trans i18nKey="emp.profile.needApproved" components={{ b: <span className="font-medium text-foreground" /> }} /></p>
                <Link to="/app/goals"><Button variant="outline" size="sm" className="mt-3 rounded-lg">{t("emp.profile.createGoal")}</Button></Link>
              </div>
            ) : (
              <div className="mt-4 space-y-4">
                <div className="space-y-1.5">
                  <Label>{t("emp.profile.learningGoal")}</Label>
                  <Select value={needId} onValueChange={setNeedId}>
                    <SelectTrigger data-testid="booking-need" className="h-11 rounded-lg"><SelectValue placeholder={t("emp.profile.selectGoal")} /></SelectTrigger>
                    <SelectContent>{needs.map((n) => <SelectItem key={n.need_id} value={n.need_id}>{n.title}</SelectItem>)}</SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>{t("emp.profile.pickTime")}</Label>
                  {dayKeys.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2">{t("emp.profile.noSlots")}</p>
                  ) : (
                    <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                      {dayKeys.map((day) => (
                        <div key={day}>
                          <p className="text-xs font-medium text-muted-foreground mb-1.5">{fmtDate(day, { weekday: "long", month: "long", day: "numeric" })}</p>
                          <div className="grid grid-cols-3 gap-1.5">
                            {slotsByDay[day].map((s) => (
                              <button key={s.id} data-testid={`slot-${s.id}`} onClick={() => setSlotId(s.id)}
                                className={`text-sm py-2 rounded-lg border font-medium transition-colors ${slotId === s.id ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary/40 hover:bg-primary/5"}`}>
                                {fmtTime(s.start)}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label>{t("emp.profile.topic")} <span className="text-muted-foreground font-normal">{t("emp.profile.topicOptional")}</span></Label>
                  <Input data-testid="booking-topic" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder={t("emp.profile.topicPlaceholder")} className="h-11 rounded-lg" />
                </div>

                {(dayRate || hasBudget) && (
                  <div data-testid="booking-cost" className="rounded-lg border border-border bg-secondary/30 p-3.5 space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium"><Wallet size={15} className="text-primary" /> {t("emp.profile.costTitle")}</div>
                    {dayRate ? (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{selectedSlot ? t("emp.profile.sessionCost", { hours: sessionHours }) : t("emp.profile.estRate")}</span>
                        <span data-testid="booking-cost-value" className="font-semibold tabular">{sessionCost != null ? fmtMoney(sessionCost, currency) : `${fmtMoney(Math.round(dayRate / dayHours), currency)}/h`}</span>
                      </div>
                    ) : null}
                    {hasBudget && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">{t("emp.profile.yourRemaining")}</span>
                        <span data-testid="booking-remaining" className={`font-semibold tabular ${overBudget ? "text-destructive" : "text-success"}`}>{fmtMoney(budget.remaining, currency)}</span>
                      </div>
                    )}
                    {overBudget && (
                      <p data-testid="booking-over-budget" className="text-xs text-destructive flex items-center gap-1.5 pt-0.5"><AlertTriangle size={13} className="shrink-0" /> {t("emp.profile.overBudget")}</p>
                    )}
                  </div>
                )}

                <Button data-testid="booking-submit" onClick={book} disabled={busy || !slotId} className="w-full h-11 rounded-lg gap-1.5 active:scale-[0.98] transition-transform">
                  {busy ? <Spinner /> : <>{t("emp.profile.requestSession")}</>}
                </Button>
                <p className="text-xs text-muted-foreground text-center">{t("emp.profile.confirmNote")}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Section({ title, icon: Icon, children, className = "" }) {
  return (
    <div className={`card-milled rounded-[0.8rem] p-6 sm:p-7 animate-fade-up ${className}`}>
      <div className="flex items-center gap-3 mb-5">
        {Icon && (
          <span className="grid place-items-center w-9 h-9 rounded-lg bg-secondary/80 text-primary shrink-0">
            <Icon size={17} strokeWidth={1.75} />
          </span>
        )}
        <h3 className="text-[0.95rem] font-semibold tracking-tight text-foreground">{title}</h3>
      </div>
      {children}
    </div>
  );
}
